// frontend/lib/auth/types.ts

export type UserRole = "nurse" | "facility" | "admin";

export interface User {
  id: string;
  email: string;
  role: UserRole;
  is_verified: boolean;
  created_at?: string;
}

/** Raw response from backend uses uppercase roles */
interface RawUser {
  id: string;
  email: string;
  role: string; // "NURSE" | "FACILITY" | "ADMIN"
  is_verified: boolean;
  created_at?: string;
}

export interface LoginResponse {
  access_token: string;
  refresh_token?: string | null;
  user: RawUser;
}

export interface RegisterRequest {
  email: string;
  password: string;
  role: "nurse" | "facility";
  // Nurse-specific fields
  first_name?: string;
  last_name?: string;
  phone?: string;
  license_state?: string;
  license_number?: string;
  specialty?: string;
  years_experience?: number;
  // Facility-specific fields
  facility_name?: string;
  facility_type?: string;
  contact_person?: string;
  address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
}

/** Transform backend role string to frontend role */
export function normalizeRole(role: string): UserRole {
  const upper = role.toUpperCase();
  if (upper === "NURSE") return "nurse";
  if (upper === "FACILITY_USER" || upper === "FACILITY") return "facility";
  if (upper === "ADMIN") return "admin";
  return role.toLowerCase() as UserRole;
}

/** Transform frontend register data to backend schema */
export function toBackendRegisterPayload(data: RegisterRequest): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    email: data.email,
    password: data.password,
    account_type: data.role.toUpperCase(),
  };

  if (data.role === "nurse") {
    payload.nurse_profile = {
      first_name: data.first_name || "",
      last_name: data.last_name || "",
      primary_specialty: data.specialty || null,
      primary_state: data.license_state || null,
    };
  } else if (data.role === "facility") {
    payload.facility_profile = {
      facility_name: data.facility_name || "",
      type: data.facility_type || null,
      primary_state: data.state || null,
    };
  }

  return payload;
}

/** Normalize raw user from API response */
export function normalizeUser(raw: RawUser): User {
  return {
    id: raw.id,
    email: raw.email,
    role: normalizeRole(raw.role),
    is_verified: raw.is_verified,
    created_at: raw.created_at,
  };
}

export interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  register: (data: RegisterRequest) => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<void>;
}
